import {
  Button,
  CopyToClipboardButton,
  EmptyState,
  FlexBox,
  Icon,
  IconButton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import type {
  AIInsightInstrumentPrompt,
  AIInsightInstrument as InsightInstrumentT,
  InstrumentPanel,
} from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useState } from 'react';

import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { aiApi } from 'apis/ai';
import { appActions } from 'app/app.actions';
// eslint-disable-next-line no-restricted-imports
import { SafeInnerHtmlSetter } from 'components/shared/util/SafeInnerHtmlSetter';
import { markdownToHtml } from 'utils/markdown';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './aiInsightInstrument.module.scss';

export type AIInsightInstrumentProps = {
  /** The containing panel. */
  panel: InstrumentPanel;
  /** The insight instrument. */
  instrument?: InsightInstrumentT;
  /** The prompt used to generate the insight. */
  prompt?: AIInsightInstrumentPrompt | null;
  /** The id of the backend request that generated the insight. */
  requestId?: string;
  /** Called when the insight should be regenerated. */
  onRegenerate: () => void;
};

type AIInsightInstrumentFeedback = 'useful' | 'not_useful';

export const AIInsightInstrument = memo(function AIInsightInstrument({
  instrument,
  panel,
  prompt,
  requestId,
  onRegenerate,
}: AIInsightInstrumentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { dashboard } = useAnalyticsState(panel);

  const [feedback, setFeedback] = useState<AIInsightInstrumentFeedback | null>(
    null,
  );

  const handleRegenerate = useCallback(() => {
    if (!prompt) return;

    onRegenerate();
    setFeedback(null);

    ampli.analyticsInsightsRegenerateClicked({
      dashboard: dashboard.title,
      panel: panel.title,
      prompt: prompt.title,
    });
  }, [dashboard.title, onRegenerate, panel.title, prompt]);

  const handleCopyToClipboard = useCallback(() => {
    if (!prompt) return;

    ampli.analyticsInsightsCopyClicked({
      dashboard: dashboard.title,
      panel: panel.title,
      prompt: prompt.title,
    });
  }, [dashboard.title, panel.title, prompt]);

  const handleFeedback = useCallback(
    (feedback: AIInsightInstrumentFeedback) => {
      if (!prompt) return;

      setFeedback(feedback);

      ampli.analyticsInsightsFeedbackClicked({
        dashboard: dashboard.title,
        panel: panel.title,
        prompt: prompt.title,
        feedback,
        requestId,
      });

      if (instrument?.feedback_id) {
        dispatch(
          aiApi.endpoints.createAiFeedback.initiate({
            feedbackId: instrument.feedback_id,
            feedback: [
              { type: 'rating', value: feedback === 'useful' ? 'good' : 'bad' },
            ],
          }),
        );
      }
    },
    [
      dashboard.title,
      dispatch,
      instrument?.feedback_id,
      panel.title,
      prompt,
      requestId,
    ],
  );

  const handleAdditionalFeedback = useCallback(async () => {
    if (!prompt || !feedback) return;

    const { isSent, feedbackMessage } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            title: 'Analytics AI Insights Feedback',
            team: 'insights',
            placeholder:
              feedback === 'useful'
                ? 'What do you like about the summary?'
                : 'What was the issue with the summary? How could it be improved?',
            extra: {
              panel: panel.title,
              prompt: prompt.title,
              feedback,
              feedbackId: instrument?.feedback_id,
              requestId,
            },
          },
        },
      }),
    );

    if (isSent && feedbackMessage && instrument?.feedback_id) {
      dispatch(
        aiApi.endpoints.createAiFeedback.initiate({
          feedbackId: instrument.feedback_id,
          feedback: [
            {
              type: 'rating',
              value: feedback === 'useful' ? 'good' : 'bad',
            },
            {
              type: 'comment',
              value: feedbackMessage,
            },
          ],
        }),
      );
    }
  }, [
    dispatch,
    feedback,
    instrument?.feedback_id,
    panel.title,
    prompt,
    requestId,
  ]);

  const handleThumbsUp = useCallback(() => {
    handleFeedback('useful');
  }, [handleFeedback]);

  const handleThumbsDown = useCallback(() => {
    handleFeedback('not_useful');
  }, [handleFeedback]);

  if (!prompt || !instrument) {
    return (
      <div className={styles.root}>
        <EmptyState
          className={styles.empty}
          icon="ai"
          title="Ready to generate AI insights"
          description="Choose one of the available insight prompts to generate AI insights."
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <FlexBox flexDirection="column" gap="300">
        <FlexBox alignItems="center" gap="300">
          <Icon
            name={prompt.icon ?? 'ai'}
            color={colors.purple[700]}
            size={24}
          />

          <Text.Heading variant="heading-3" className={styles.title}>
            {prompt.title}
          </Text.Heading>

          <FlexBox
            gap="200"
            flex="1"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Tooltip content="Regenerate insight">
              <IconButton
                onClick={handleRegenerate}
                variant="tertiary"
                icon="sync"
              />
            </Tooltip>

            <Tooltip content="Copy text to clipboard">
              <CopyToClipboardButton
                textToCopy={instrument.data}
                tooltip={false}
                clickedProps={{ icon: 'done' }}
                buttonProps={{ variant: 'tertiary', icon: 'copy' }}
                onClick={handleCopyToClipboard}
              />
            </Tooltip>

            {feedback ? (
              <Button
                onClick={handleAdditionalFeedback}
                variant="tertiary"
                icon={feedback === 'useful' ? 'thumbs-up' : 'thumbs-down'}
              >
                Tell us more
              </Button>
            ) : (
              <>
                <Tooltip content="This insight is not helpful">
                  <IconButton
                    onClick={handleThumbsDown}
                    variant="tertiary"
                    icon="thumbs-down"
                  />
                </Tooltip>

                <Tooltip content="This insight is helpful">
                  <IconButton
                    onClick={handleThumbsUp}
                    variant="tertiary"
                    icon="thumbs-up"
                  />
                </Tooltip>
              </>
            )}
          </FlexBox>
        </FlexBox>

        <Text.Paragraph as="div" variant="2" subdued>
          <SafeInnerHtmlSetter
            element="div"
            content={markdownToHtml(instrument.data)}
          />
        </Text.Paragraph>
      </FlexBox>
    </div>
  );
});
